<template>
    <div id="main">
        <div class="alert-wrapper">
            <alert-container :messages="msgs" />
        </div>

        <!-- Título -->
        <div class="atendimento">
            <h1 class="title">{{$t('percentual-participacao')}}</h1>
            <nav class="breadcrumb" aria-label="breadcrumbs">
                <ul>
                    <li><a>{{$t('vendas')}}</a></li>
                    <li><a>{{$t('relatorios')}}</a></li>
                    <li><a href="#" aria-current="page">{{$t('percentual-participacao')}}</a></li>
                </ul>
            </nav>
        </div>

        <!-- Filtros -->
        <div class="columns is-multiline">
            <div class="column is-6">
                <label v-text="$t('tipo-analise')"/>
                <v-select
                    ref="tipoAnalise"
                    v-model="tipoAnalise"
                    :options="tipoAnalises"
                    :clearable="false"
                >
                    <template slot="option" slot-scope="option">
                        {{ $t(option.label) }}
                    </template>
                    <template slot="selected-option" slot-scope="option">
                        {{ $t(option.label) }}
                    </template>
                    <template slot="no-options">{{$t('sem-resultados')}}</template>
                </v-select>
            </div>
            <div class="column is-6">
                <v-input
                    title="referencia"
                    ref="refInput"
                    v-mask="['##/####']"
                    placeholder="periodo-placeholder"
                    v-model="ref"
                />
            </div>
            <div class="column">
                <label v-text="$t('sellers')"/>
                <v-select
                    ref="vendedorInput"
                    class="no-clear"
                    v-model="vendedoresSelected"
                    :options="vendedoresOptions"
                    :multiple="true"
                    :loading="vendedoresLoading"
                    @search="loadVendedores"
                >
                    <template slot="no-options">{{ $t('sem-resultados-filtro') }}</template>
                </v-select>
            </div>
            <div class="column">
                <vss-select
                    title="tipo-pedido"
                    placeholder="selecione"
                    :options="tipoPedidoList"
                    v-model="tipoPedidoSelected"
                    :loading="tipoPedidoLoading"
                    @search="carregarTipoPedido"
                    @input="debouncedOnSearch"
                    :height="40"
                    multiple
                    searchable
                    clearable
                    no-clearfix
                    :closeOnSelect="false"
                >
                    <template #multiple-selected="{ selected }">
                        {{$t('selecionado')}} ({{  selected && selected.length > 0 ? selected.length : $t('todos') }})
                    </template>
                </vss-select>
            </div>
        </div>
        <div class="columns mt-0 mb-2">
            <div class="column is-6 has-text-centered">
                <button class="delete-btn pl-4 pr-4" @click="limparFiltros">
                    {{ $t('limpar-filtros') }}
                </button>
            </div>
            <div class="column is-6 has-text-centered">
                <button class="save-btn pl-4 pr-4" @click="getDados">
                    {{ $t('pesquisar') }}
                </button>
            </div>
        </div>
        
        <div class="chart-content" v-if="reportData.length > 0 && !isSearching">
            <chart-percentual-participacao
                :data="reportData"
                :total="totalVendas"
            />
        </div>

        <!-- Tabela -->
        <div class="page results" v-if="reportData.length > 0 && !isSearching">
            <b-table
                :data="reportData"
                :paginated="true"
                :per-page="perPage"
                :current-page.sync="currentPage"
                class="data-table"
            >
                    <b-table-column v-for="(f, i) in tableFields"
                        :key="i"
                        :field="f.key"
                        :label="$t(f.label)" 
                        :sortable="f.sortable"
                        :width="f.width"
                        :numeric="f.numeric"
                        v-slot="props"
                    >
                        <div v-if="f.key == 'total'">
                            {{ getDecimal(props.row[f.key] , null, true) }}
                        </div>
                        <div v-else-if="f.key == 'porc'">
                            {{ getDecimal(props.row[f.key])  }}%
                        </div>
                        <div v-else>
                            {{ props.row[f.key] }}
                        </div>
                    </b-table-column>
                <template slot="footer">
                    <th class="is-hidden-mobile">{{$t('total')}}</th>
                    <th class="is-hidden-mobile has-text-right">{{ getDecimal(totalVendas , null, true) }}</th>
                    <th class="is-hidden-mobile"></th>
                </template>
            </b-table>
        </div>
        
        <!-- Loader -->
        <div
            v-show="isSearching"
            class="page searching-loader"
        >
            <svg class="loading">
                <use :xlink:href="require('@/assets/img/icons/loader.svg')+'#layer'"></use>
            </svg>
            <div>
                {{ $t('carregando-dados') }}...
            </div>
        </div>

        <!-- Mensagem 'não há dados' e ajuda de utilização -->
        <div
            v-show="(firstTime || noData) && !isSearching"
            class="page searching-loader"
        >
            <svg class="bigicon">
                <use v-if="firstTime" :xlink:href="require('@/assets/img/icons/magnify.svg')+'#layer'"></use>
                <use v-else :xlink:href="require('@/assets/img/icons/chart-error.svg')+'#layer'"></use>
            </svg>
            <div v-if="firstTime">
                {{ $t('descricao-ajuda-filtro-relatorio') }}
            </div>
            <div v-else>
                {{ $t('no-data') }}
            </div>
        </div>

    </div>
</template>

<script>
import ChartPercentualParticipacao from "./ChartPercentualParticipacao";
import FiltroRelatorioService from "@/services/FiltroRelatorioService";
import VendasRelPercentualParticipacao from "@/services/VendasRelPercentualParticipacao";
import TipoPedidoService from "@/services/vendas/TipoPedidoService.js";
import Message from "@/models/Message";
import ColaboradorService from "@/services/ColaboradorService";

export default {
    components: {
        ChartPercentualParticipacao
    },
    data() {
        return {
            tipoPedidoService: new TipoPedidoService(this.$http, this.$router),
            tipoAnalises: [
                { id: 0, label: 'pedido' },
                { id: 1, label: 'faturamento' },
            ],
            tipoAnalise: null,
            filtroService: null,
            dataService: null,
            ref: '',
            refPesq: '',
            situacoes: [{id: 0, label: 'nao-superada'}, {id: 1, label: 'superada'}],
            situacao: {id: 1, label: 'superada'},
            vendedoresOptions: [],
            vendedoresSelected: null,
            vendedoresLoading: false,
            tableFields: [
                {key: 'nome',       label: 'nome',                      sortable: false},
                {key: 'total',      label: 'total',      width: 150,    sortable: false, numeric: true},
                {key: 'porc',       label: 'percentual', width: 150,    sortable: false, numeric: true}
            ],
            perPage: 20,
            currentPage: 1,
            reportData: [],
            totalVendas: 0,
            firstTime: true,
            noData: false,
            isSearching: false,
            msgs: [],
            permissao: {},
            colaboradorService: null,
            tipoPedidoList: [],
            tipoPedidoSelected: [],
            tipoPedidoLoading: false,
        };
    },
    created () {
        this.tipoAnalise = this.tipoAnalises[0];
        this.permissao = this.hasPermissionAccess('vendas', 'relatorio-analitico', 'relatorio-vendas-percentual-participacao');

        !this.permissao.read && this.$router.push({
            name: 'acesso-nao-permitido',
            params: { tela: 'percentual-participacao' },
        });

        let d = new Date();
        
        this.filtroService = new FiltroRelatorioService(this.$http, this.$router);
        this.dataService = new VendasRelPercentualParticipacao(this.$http, this.$router);
        this.colaboradorService = new ColaboradorService(this.$http, this.$router);
        this.loadVendedores('');
        this.ref = this.getPeriodoStringFromDate(d);
    },
    methods: {
        showMessage (type, message) {
            this.msgs.push(
                new Message(type, message, true)
            );
        },
        loadVendedores (q) {
            this.vendedoresLoading = true;

            /*
                @70881 - alterado para trazer a lista de vendedores vinculados ao usuario.
            */
            this.colaboradorService.findListAssociated(q, 5000000)

                .then(r => {
                    if (r.status != 200) {
                        console.error(`Erro com status ${r.status} ao realizar pesquisa por vendedor. Termo: [${q}].`);
                        this.vendedoresOptions = [];
                    } else {
                        this.vendedoresOptions = r.content.map(v => {
                            return {
                                id: v.id,
                                label: v.nome.toLocaleUpperCase()
                            }
                        });
                    }
                })
                .catch(e => {
                    console.error(`Erro ao realizar pesquisa por vendedor. Termo: [${q}].`, e);
                    this.vendedoresOptions = [];
                })
                .finally(() => {
                    this.vendedoresLoading = false;
                });
        },
        getPeriodoStringFromDate (d) {
            return `${d.getMonth() < 9 ? '0' : ''}${d.getMonth() + 1}/${d.getFullYear()}`;
        },
        limparFiltros () {
            this.vendedoresSelected = [];
            this.tipoPedidoSelected = [];
        },
        getDados () {
            if (this.validateDatas()) {
                let filtro = {};
                this.isSearching = true;
                if (this.firstTime) this.firstTime = false;  
    
                filtro.referencia = this.ref;
                filtro.vendedores = this.vendedoresSelected != null ? this.vendedoresSelected.map(v => v.id) : [];
                filtro.tiposPedido = this.tipoPedidoSelected.map(v => v.id);
                filtro.tipoAnalise = this.tipoAnalise.label;
                
                this.dataService.load(filtro)
                    .then(r => {
                        if (r.status != 200) {
                            console.error(`Erro ${r.status} ao carregar dados de percentual de participação.`);
                            this.showMessage('error', 'msg-erro-relatorio');
                            this.reportData = [];
                            this.tableData = [];
                            this.noData = true;
                        } else {
                            this.noData = r.content.length == 0;
                            this.reportData = this.calculatePerc(r.content);
                            this.refPesq = this.ref;
                        }
                    })
                    .catch(e => {
                        console.error('Erro ao carregar dados de percentual de participação.', e);
                        this.showMessage('error', 'msg-erro-relatorio');
                        this.reportData = [];
                        this.tableData = [];
                        this.noData = true;
                    })
                    .finally(() => {
                        this.isSearching = false;
                    });
            }
        },
        calculatePerc (data) {
            let total = data.reduce((ac, cv) => ac + cv.total, 0);

            data.forEach(v => {
                v.porc = (v.total / total)*100;
            });

            this.totalVendas = total;

            return data;
        },
        validateDatas() {
            let dr = /\d\d\/\d\d\d\d/; // regex para dd/dddd
            let valid = true;
            let dsplit = null;

            /* Validando período de início */
            if (!dr.test(this.ref)) {
                console.error(`Validação falhou no período de referência, data inválida: [${this.ref}]`);
                this.showMessage('error', 'msg-erro-periodo-referencia');
                valid = false;
            } else {
                dsplit = this.ref.split('/');

                dsplit[0] = parseInt(dsplit[0]); // Mês
                dsplit[1] = parseInt(dsplit[1]); // Ano
            }

            if (dsplit !== null) {
                if ((dsplit[0] < 1 && dsplit[0] > 12) || (dsplit[1] < 1900 || dsplit[1] > 2200)) {
                    console.error(`Validação falhou no período de referência, data inválida: [${this.ref}]`);
                    this.showMessage('error', 'msg-erro-periodo-referencia');
                    valid = false;
                }
            }

            return valid;
        },
        carregarTipoPedido(query = null) {
            this.tipoPedidoLoading = true;
            query = this.isNull(query) ? ".*" : this.removeToSearch(query);

            this.tipoPedidoService
                .findAllCombo(query)
                .then(this.mapTipoPedidoList)
                .then((value) => {
                    if (value != null) {
                        this.tipoPedidoList = value;
                    }
                })
                .catch((e) => {
                    this.$root.showMessage("error", "msg-erro-load", true);
                })
                .finally((_) => {
                    this.tipoPedidoLoading = false;
                });
        },
        mapTipoPedidoList(response) {
            return response.content.map((m) => {
                return {
                    id: m.id,
                    label: m.descricao,
                };
            });
        },
    }
};
</script>

<style lang="scss" scoped>
@import "../../../../mixins.scss";
$padding: 5px;

#main {
    padding: 0 15px 15px 20px;
    height: auto;
    font-size: 13px;
}
.page {
    position: relative;
    &.filtros-main {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        .referencia {
            width: calc(50% - #{$padding});
            @include default-size-column($p-bottom: 0.5rem);
        }
        .vendedores {
            width: 100%;
            @include default-size-column($p-bottom: 0.5rem);
        }
        input {
            height: 27px;
        }
    }
    &.control-buttons {
        display: flex;
        align-items: flex-end;
        justify-content: space-around;
        width: 100%;
        height: 30px;
    }
    &.results {
        padding-top: 1rem;
        margin-top: 15px;

        .data-table {
            padding: 0;
        }
    }

}
.chart-content {
    background-color: $sovis-white;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    padding: 0.6rem 1rem 1rem 1rem;
}
.control-buttons {
    margin: 0;
}
.searching-loader {
    margin-top: 20px;
}

</style>